import React, { useEffect } from 'react';
import { Info, WarningCircle, X } from '@phosphor-icons/react';

// A small in-app alert/notice modal. Drop-in replacement for `window.alert()`,
// which Brave (and Firefox with "prevent additional dialogs" checked) silently
// suppresses inside the iframe sandbox, leaving the user with no feedback.
//
// Usage:
//   const [notice, setNotice] = useState(null);
//   setNotice({ title: 'Save failed', message: 'Pick a customer first.' });
//   <AlertDialog open={!!notice} title={notice?.title} message={notice?.message}
//                onClose={() => setNotice(null)} />

export const AlertDialog = ({
  open,
  title = 'Notice',
  message,
  variant = 'info', // 'info' | 'warning'
  buttonLabel = 'OK',
  onClose,
  testIdPrefix = 'alert-dialog',
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const Icon = variant === 'warning' ? WarningCircle : Info;
  const iconClass = variant === 'warning' ? 'text-danger shrink-0' : 'text-gold shrink-0';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={`${testIdPrefix}`}
      onClick={() => onClose?.()}
    >
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5 border-b border-border-color">
          <Icon size={24} weight="duotone" className={iconClass} />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-text-primary" data-testid={`${testIdPrefix}-title`}>{title}</h3>
            {message && (
              <p className="text-sm text-text-secondary mt-1 whitespace-pre-line" data-testid={`${testIdPrefix}-message`}>
                {message}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid={`${testIdPrefix}-close`}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex items-center justify-end p-4">
          <button
            onClick={onClose}
            data-testid={`${testIdPrefix}-ok`}
            className="px-4 py-2 text-sm font-bold rounded-lg bg-gold text-black hover:bg-gold-hover"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDialog;
